/** Header-action builder for the BOQ (Bill of Quantities) pricing tab.
 *  Desktop gets Search; mobile search lives below the header. Every viewport gets Export.
 *  Bill management, Create (+/Import popover) and Delete selected are desktop only. Apply OH&P is held back
 *  until it ships. No JSX. */

import {
  Search,
  Plus,
  FilePlus,
  Upload,
  Trash2,
  Download,
  Loader2,
  LayoutGrid,
} from '@/shared/ui/icons';
import type { MobileHeaderAction, MobileHeaderSubAction } from './types';

/** Context required by the Approve BOQ pill in the global header.
 *  Only populated when both `projectId` and `currentUserId` are known —
 *  the bridge omits this entirely otherwise, so the pill stays unmounted.
 *  `boqStatus` and `projectStage` stay string-typed because the upstream
 *  document includes legacy values (`'issued'`, `'superseded'`) the pill
 *  treats as non-approved. Notification recipients (`internalUserIds`)
 *  come from `projectTabProps` in the header — they're scoped to the
 *  project's assigned staff, not threaded through the BOQ bridge. */
export interface BOQApprovalContext {
  projectId: string;
  currentUserId: string;
  projectName?: string;
  projectStage?: string;
  boqStatus?: string;
  isClient: boolean;
  currentUserName?: string;
}

export interface BOQHeaderState {
  canImport: boolean;
  canAddSheet?: boolean;
  canApplyOHP?: boolean;
  canExport: boolean;
  isExporting: boolean;
  selectedCount: number;
  bills: Array<{ id: string; label: string }>;
  activeBillId?: string | null;
  activeBillLabel?: string;
  /** Contextual copy for the persistent global-header search field. */
  searchPlaceholder?: string;
  canManageBills?: boolean;
  summaryTabs?: Array<{ id: string; label: string }>;
  filterMode?: 'all' | 'priced' | 'unpriced' | 'excluded' | 'unlinked';
  /** Measured lines in the bill on screen with no takeoff linked. */
  takeoffUnlinkedCount?: number;
  approval?: BOQApprovalContext;
  /** Grand total for the active bill — shown in the desktop header title slot. */
  activeBillTotal?: number;
  overviewOpen?: boolean;
}

export interface BOQHeaderCtx {
  boqHeaderState: BOQHeaderState;
  isDesktopViewport: boolean;
  /** Mutates the provided arrays in-place; caller owns initialisation. */
  leadingActions: MobileHeaderAction[];
  trailingActions: MobileHeaderAction[];
}

/** Appends BOQ pills to the caller's leading/trailing action arrays.
 *  Called only when `boqHeaderState` is non-null and the project-detail
 *  tab is the BOQ pricing page. */
export function buildBOQHeaderActions(ctx: BOQHeaderCtx): void {
  const { boqHeaderState, isDesktopViewport, trailingActions } = ctx;

  // Mobile uses the page search so typing leaves the title and navigation visible.
  const boqSearchAction = {
    id: 'boq-search',
    icon: Search,
    ariaLabel: 'Search items',
    group: 'view' as const,
    onClick: () => window.dispatchEvent(new CustomEvent('boq:open-search')),
  };
  if (isDesktopViewport) {
    trailingActions.push(boqSearchAction);
  }

  trailingActions.push({
    id: 'boq-overview', icon: LayoutGrid, ariaLabel: 'Pricing overview',
    active: boqHeaderState.overviewOpen, group: 'view',
    onClick: () => window.dispatchEvent(new CustomEvent('boq:toggle-overview')),
  });

  // Bill rename and delete sit on each bill's row in the Pages summary, not in the header.

  // Create group — desktop-only.
  if (isDesktopViewport) {
    const createSubActions: MobileHeaderSubAction[] = [
      {
        id: 'boq-add',
        icon: Plus,
        label: 'New Row',
        onClick: () => window.dispatchEvent(new CustomEvent('boq:add-item')),
      },
    ];
    if (boqHeaderState.canAddSheet) {
      createSubActions.push({
        id: 'boq-add-sheet',
        icon: FilePlus,
        label: 'New Sheet…',
        onClick: () => window.dispatchEvent(new CustomEvent('boq:add-sheet')),
      });
    }
    if (boqHeaderState.canImport) {
      createSubActions.push({
        id: 'boq-import',
        icon: Upload,
        label: 'Import from File…',
        onClick: () => {
          window.dispatchEvent(new CustomEvent('boq:navigate-to-import'));
        },
      });
    }
    trailingActions.push({
      id: 'boq-create',
      icon: Plus,
      ariaLabel: 'Add or import',
      group: 'create',
      subActions: createSubActions,
    });
  }

  // Delete-selected pill — desktop-only, shown only with an active selection.
  // OH&P is mid-rework and intentionally suppressed from the header until it
  // ships — a permanently greyed control reads as broken chrome, not
  // "unavailable right now". Re-add it here with group:'modify' (alongside
  // Delete) when it lands.
  if (isDesktopViewport) {
    if (boqHeaderState.selectedCount > 0) {
      trailingActions.push({
        id: 'boq-delete-selected',
        icon: Trash2,
        badge: boqHeaderState.selectedCount,
        ariaLabel: `Delete ${boqHeaderState.selectedCount} selected`,
        onClick: () => window.dispatchEvent(new CustomEvent('boq:delete-selected')),
        group: 'modify',
      });
    }
  }

  // Export — available on every viewport.
  if (boqHeaderState.canExport) {
    trailingActions.push({
      id: 'boq-export',
      icon: boqHeaderState.isExporting ? Loader2 : Download,
      ariaLabel: 'Export',
      onClick: () => window.dispatchEvent(new CustomEvent('boq:open-export')),
      group: 'output',
    });
  }
}
